export interface Pagination {
  totalItemCount: number;
  pageNumber: number;
  pageSize: number;
  isLastPage: boolean;
}

export enum ExportFormatCode {
  Csv = 1,
  Excel = 2,
  Pdf = 3,
}

export interface ExportQuery {
  format: ExportFormatCode;
  isPrint?: boolean;
  ids?: string[];
  searchText?: string | null;
  statusType?: number;
  clientType?: number | null;
  dueFilter?: number | null;
  /** ap5-a4: only read when dueFilter is 8 (custom range); yyyy-MM-dd. */
  startDate?: string | null;
  /** ap5-a4: only read when dueFilter is 8 (custom range); yyyy-MM-dd. */
  endDate?: string | null;
  filter?: number;
  /** Client status: null exports both, true only active, false only inactive. */
  isActive?: boolean | null;
}

export interface ClientFilter {
  searchText?: string;
  statusType?: number;
  clientType?: number | null;
  dueFilter?: number | null;
  /** ap5-a3: 0 lists the live clients, 1 lists the deleted ones. */
  recordStatus?: number;
  /** Client status: null lists both, true only active, false only inactive. */
  isActive?: boolean | null;
  /** ap5-a4: only read when dueFilter is 8 (custom range); yyyy-MM-dd. */
  startDate?: string | null;
  /** ap5-a4: only read when dueFilter is 8 (custom range); yyyy-MM-dd. */
  endDate?: string | null;
}

export interface ClientGridQuery extends ClientFilter {
  pageNumber: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: boolean;
}

export interface ClientGridRow {
  universalId: string;
  clientId: string;
  clientName: string;
  clientType: string;
  clientTypeId: number;
  period: string;
  dueDate: string;
  status: string;
  statusId: number | null;
  accountingPeriodId: string | null;
  registrationNo: string | null;
  utrNo: string | null;
  isActive: boolean;
  /**
   * task-29: true when the client holds trial balance lines or journals. Client/deleteClients
   * refuses such a client with a 409, so the grid uses this to say so before asking. Optional
   * because the grid only returns it once usp_Client_Grid is deployed; the API refusal is the
   * restriction, this is only what spares the user a pointless round trip.
   */
  hasAccountingData?: boolean;
}

export interface ClientGridResponse {
  clientList: ClientGridRow[];
  pagination: Pagination;
}

export interface ClientAddress {
  addressType: number;
  addressLine: string;
  city: string;
  county: string | null;
  country: string;
  postCode: string;
  isPrimary: boolean;
}

export interface ClientContact {
  contactId: string | null;
  title: string | null;
  fullName: string;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
}

export interface ClientDetail {
  id: string;
  clientId: string;
  autoClientId: number;
  clientType: number;
  companyName: string;
  companyType: string | null;
  nationalInsuranceNumber: string | null;
  isCommunityInterest: boolean;
  businessStartDate: string | null;
  bookStartDate: string | null;
  yearEnd: string | null;
  registrationNumber: string | null;
  utrNo: string | null;
  vatScheme: string | null;
  vatRegNo: string | null;
  vatRegistrationDate: string | null;
  vatSubmitType: string | null;
  vatSubmitTypeValue: string | null;
  payeRefNo: string | null;
  /** ap5-a3: HMRC Accounts Office reference, e.g. 123PA00123456. */
  accountsOfficeRefNo: string | null;
  authorisationCode: string | null;
  autoPeriodGeneration: boolean;
  addresses: ClientAddress[];
  contacts: ClientContact[];
  /** APT378: the client's assigned manager; the API does not send it yet, so the dashboard shows it only when it is there. */
  clientManager?: string | null;
}

export interface SaveClientPayload {
  id: string | null;
  clientId: string;
  clientType: number;
  companyName: string;
  companyType: string | null;
  nationalInsuranceNumber: string | null;
  isCommunityInterest: boolean;
  businessStartDate: string | null;
  bookStartDate: string | null;
  yearEnd: string | null;
  registrationNumber: string | null;
  utrNo: string | null;
  vatScheme: string | null;
  vatRegNo: string | null;
  vatRegistrationDate: string | null;
  vatSubmitType: string | null;
  vatSubmitTypeValue: string | null;
  payeRefNo: string | null;
  accountsOfficeRefNo: string | null;
  addresses: ClientAddress[];
  contacts: ClientContact[];
}

/**
 * ap5-a3: a Companies House company profile, used by the Add Client lookup.
 * APT241: it carries every Companies House value the form has a field for, not only the name.
 */
export interface CompanyLookupResult {
  companyNumber: string;
  companyName: string;
  jurisdiction: string | null;
  companyStatus: string | null;
  /** APT241: Companies House's own type (ltd, plc, llp, private-limited-guarant-nsc, ...). */
  companyType?: string | null;
  /** APT241: the client type it means: 0 Limited Company, 3 LLP; null when neither. */
  clientType?: number | null;
  /** APT241: the "Type of Company": 'shares' or 'guarantee'. */
  limitedBy?: string | null;
  isCommunityInterest?: boolean | null;
  /** APT241: date_of_creation (yyyy-MM-dd), the Business Start Date. */
  incorporatedOn?: string | null;
  accountingReferenceDay?: number | null;
  accountingReferenceMonth?: number | null;
  /** APT241: the start of the next accounts period (yyyy-MM-dd), the Book Start Date. */
  nextAccountsPeriodStart?: string | null;
  /** APT241: the year end of the next accounts (yyyy-MM-dd). */
  yearEnd?: string | null;
  registeredOffice?: CompanyLookupAddress | null;
}

/** APT241: the registered office, in the shape of an Add Client address line. */
export interface CompanyLookupAddress {
  addressLine: string | null;
  city: string | null;
  county: string | null;
  country: string;
  postCode: string | null;
}

/** prod-p2: one Companies House name-search hit offered by the Add Client picker. */
export interface CompanySearchMatch {
  companyNumber: string;
  companyName: string;
  companyStatus: string | null;
  companyType: string | null;
  incorporatedOn: string | null;
  address: string | null;
}

/** prod-p2: the Add Client company search results. */
export interface CompanySearchResponse {
  companies: CompanySearchMatch[];
}

export interface SideListItem {
  id: string;
  universalId: string;
  name: string;
  value: string | null;
}

export interface SideListResponse {
  sideModuleList: SideListItem[];
  pagination: Pagination;
}

export interface AuthorisationCodeRow {
  universalId: string;
  clientCode: string;
  name: string;
  registrationNo: string | null;
  utrNo: string | null;
  authCode: string | null;
}

export interface AuthorisationCodeResponse {
  items: AuthorisationCodeRow[];
  pagination: Pagination;
}

export interface UserGridRow {
  universalId: string;
  id: string;
  userName: string;
  email: string;
  createdOn: string;
  userType: string;
  status: string;
  statusId: number;
  /**
   * task-58: false for your own account and for the firm's last live Admin, which
   * User/deleteUser refuses (422 and 409). Optional, and read as allowed when it is
   * missing: usp_User_List is not deployed yet, and defaulting to blocked would stop
   * every delete. The API refusal is the restriction; this only disables the button.
   */
  canDelete?: boolean;
}

export interface UserGridResponse {
  userList: UserGridRow[];
  pagination: Pagination;
}

export interface UserGridQuery {
  pageNumber: number;
  pageSize: number;
  searchText?: string;
  statusType?: number;
  /** ap5-a3: 0 lists the live users, 1 lists the deleted ones. */
  recordStatus?: number;
  /** prod-p2: userName, email, createdOn or userType; anything else is user ID, the default order. */
  sortBy?: string;
  /** prod-p2: true ascending. */
  sortOrder?: boolean;
}

export interface UserAddress {
  addressType: number;
  addressLine: string;
  city: string;
  county: string | null;
  country: string;
  postCode: string;
}

export interface UserDetail {
  id: string;
  userCode: string;
  userType: string;
  contactId: string | null;
  title: string | null;
  firstName: string;
  email: string;
  phone: string | null;
  status: number;
  addresses: UserAddress[];
}

export interface SaveUserPayload {
  id: string | null;
  userType: string;
  contactId: string | null;
  title: string | null;
  firstName: string;
  email: string;
  phone: string | null;
  password: string | null;
  confirmPassword: string | null;
  addresses: UserAddress[];
}

export interface ContactOption {
  id: string;
  name: string;
  clientName: string;
  email: string | null;
  phone: string | null;
}

export interface PeriodRow {
  id: string;
  fromDate: string;
  toDate: string;
  accountsDueDate: string;
  ct600DueDate: string;
  status: string;
  statusId: number;
  isLocked: boolean;
  isCeased: boolean;
  isComparative: boolean;
  periodFrom: string;
  periodTo: string;
  accountsDue: string | null;
  ct600Due: string | null;
}

export interface ClientPeriods {
  clientId: string;
  clientName: string;
  autoPeriodGeneration: boolean;
  generatedPeriods: number;
  periods: PeriodRow[];
}

export interface PeriodSuggestion {
  periodFrom: string;
  periodTo: string;
  accountsDueDate: string;
  ct600DueDate: string;
}

export interface SavePeriodPayload {
  clientId: string;
  id: string | null;
  periodFrom: string;
  periodTo: string;
  /** Omitted (null) for a sole trader or partnership: the API then uses the default (BUG-078). */
  accountsDueDate: string | null;
  ct600DueDate: string | null;
  lockPreviousPeriod: boolean;
  isCompanyCeased: boolean;
  forComparative: boolean;
}

/** ap5-a4: the Accounts dashboard for the open client (api/v1/ClientDashboard/getDashboard). */
export interface ClientDashboardSeriesPoint {
  label: string;
  value: number;
}

export interface ClientDashboardOverduePeriod {
  accountingPeriodId: string;
  period: string;
  dueDate: string;
  daysOverdue: number;
}

export interface ClientDashboardTrialBalance {
  trialBalanceId: string;
  refNo: string;
  difference: number;
}

export interface ClientDashboardActivity {
  id: number;
  date: string;
  description: string;
  userName: string;
  /** BUG-056: the instant in UTC, shown in the viewer's time zone like every other activity time. */
  occurredAtUtc: string;
}

export interface ClientDashboard {
  clientId: string;
  clientCode: string;
  clientName: string;
  clientType: string;
  totalPeriods: number;
  openPeriods: number;
  lockedPeriods: number;
  accountsOverdue: number;
  accountsDueIn30Days: number;
  ct600DueIn30Days: number;
  trialBalances: number;
  unbalancedTrialBalances: number;
  chartAccounts: number;
  periodsByStatus: ClientDashboardSeriesPoint[];
  overduePeriodList: ClientDashboardOverduePeriod[];
  unbalancedTrialBalanceList: ClientDashboardTrialBalance[];
  recentActivity: ClientDashboardActivity[];
}

/** APT378: the Financial Overview of one period (api/v1/ClientDashboard/getFinancialOverview). HTTP 200 with hasData false when the period has no trial balance. */
export interface ClientFinancialOverview {
  hasData: boolean;
  periodId: string | null;
  periodFrom: string | null;
  periodTo: string | null;
  /** Which trial balance fed the figures (as the API names it). */
  source: string | null;
  turnover: number;
  totalIncome: number;
  costOfSales: number;
  expenses: number;
}

export interface SelectedClient {
  id: string;
  name: string;
}

/** Datepicker values may be Moment objects, Dates or ISO strings; the API takes yyyy-MM-dd. */
export function toIsoDate(value: any): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value?.format === 'function') {
    return value.format('YYYY-MM-DD');
  }
  if (value instanceof Date) {
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }
  if (typeof value === 'string') {
    return value.substring(0, 10);
  }
  return null;
}

const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isGuid(value: string | null | undefined): boolean {
  return !!value && guidPattern.test(value);
}
